import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, Upload, Download, Trash2, Search, Filter, 
  File, FileSpreadsheet, Image, MessageSquare, X, Eye,
  Calendar, User, FolderOpen, BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentManagement = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filtres
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    category: 'AUTRE',
    title: '',
    description: '',
    student_id: '',
    class_id: ''
  });
  const [uploading, setUploading] = useState(false);
  
  // Comment dialog
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, selectedCategory, searchQuery]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];
    
    // Filtrer par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }
    
    // Filtrer par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.original_filename.toLowerCase().includes(query)
      );
    }
    
    setFilteredDocuments(filtered);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Fichier trop volumineux. Maximum 5MB');
        return;
      }
      setUploadFile(file);
      // Auto-remplir le titre avec le nom du fichier
      if (!uploadForm.title) {
        setUploadForm({
          ...uploadForm,
          title: file.name.replace(/\.[^/.]+$/, '')
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    if (!uploadForm.title) {
      toast.error('Veuillez saisir un titre');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadForm.category);
      formData.append('title', uploadForm.title);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.student_id) formData.append('student_id', uploadForm.student_id);
      if (uploadForm.class_id) formData.append('class_id', uploadForm.class_id);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploadé avec succès');
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchDocuments();
      fetchStats();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadForm({
      category: 'AUTRE',
      title: '',
      description: '',
      student_id: '',
      class_id: ''
    });
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/documents/${doc.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Téléchargement réussi');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Document supprimé');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error('Veuillez saisir un commentaire');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/documents/${selectedDocument.id}/comment`,
        { comment: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Commentaire ajouté');
      setCommentDialogOpen(false);
      setCommentText('');
      fetchDocuments();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const getFileIcon = (fileType, filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return <FileText className="h-8 w-8 text-red-600" />;
    if (['docx', 'doc'].includes(ext)) return <FileText className="h-8 w-8 text-blue-600" />;
    if (['xlsx', 'xls'].includes(ext)) return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
    if (['png', 'jpg', 'jpeg'].includes(ext)) return <Image className="h-8 w-8 text-purple-600" />;
    return <File className="h-8 w-8 text-gray-600" />;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'ADMINISTRATIF': 'Administratif',
      'PEDAGOGIQUE': 'Pédagogique',
      'SCOLAIRE': 'Scolaire',
      'FINANCIER': 'Financier',
      'AUTRE': 'Autre'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'ADMINISTRATIF': 'bg-blue-100 text-blue-800',
      'PEDAGOGIQUE': 'bg-green-100 text-green-800',
      'SCOLAIRE': 'bg-purple-100 text-purple-800',
      'FINANCIER': 'bg-yellow-100 text-yellow-800',
      'AUTRE': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['AUTRE'];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Chargement des documents...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Documents</p>
                  <p className="text-2xl font-bold">{stats.total_documents}</p>
                </div>
                <FolderOpen className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Uploads Récents</p>
                  <p className="text-2xl font-bold">{stats.recent_uploads}</p>
                </div>
                <Upload className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Par Catégorie</p>
                  <p className="text-sm font-medium">
                    {Object.keys(stats.by_category || {}).length} types
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Top Downloads</p>
                  <p className="text-sm font-medium">
                    {stats.top_downloads?.length || 0} docs
                  </p>
                </div>
                <Download className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Gestion des Documents
            </CardTitle>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtres */}
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Recherche */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher un document..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Catégorie */}
              <div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes catégories</SelectItem>
                    <SelectItem value="ADMINISTRATIF">Administratif</SelectItem>
                    <SelectItem value="PEDAGOGIQUE">Pédagogique</SelectItem>
                    <SelectItem value="SCOLAIRE">Scolaire</SelectItem>
                    <SelectItem value="FINANCIER">Financier</SelectItem>
                    <SelectItem value="AUTRE">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Liste des documents */}
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">Aucun document trouvé</p>
              <p className="text-sm text-gray-500 mt-1">
                {searchQuery || selectedCategory !== 'all' 
                  ? 'Essayez de modifier vos filtres'
                  : 'Commencez par uploader un document'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => (
                <Card key={doc.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Icône et Titre */}
                      <div className="flex items-start space-x-3">
                        {getFileIcon(doc.file_type, doc.original_filename)}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate" title={doc.title}>
                            {doc.title}
                          </h3>
                          <p className="text-xs text-gray-500 truncate" title={doc.original_filename}>
                            {doc.original_filename}
                          </p>
                        </div>
                      </div>

                      {/* Catégorie */}
                      <Badge className={getCategoryColor(doc.category)}>
                        {getCategoryLabel(doc.category)}
                      </Badge>

                      {/* Description */}
                      {doc.description && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {doc.description}
                        </p>
                      )}

                      {/* Métadonnées */}
                      <div className="space-y-1">
                        <div className="flex items-center text-xs text-gray-500">
                          <User className="h-3 w-3 mr-1" />
                          {doc.uploaded_by_name}
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(doc.upload_date)}
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <Download className="h-3 w-3 mr-1" />
                          {doc.download_count} téléchargement{doc.download_count !== 1 ? 's' : ''}
                        </div>
                        <div className="text-xs text-gray-500">
                          Taille: {formatFileSize(doc.file_size)}
                        </div>
                      </div>

                      {/* Commentaires */}
                      {doc.comments && doc.comments.length > 0 && (
                        <div className="flex items-center text-xs text-gray-500">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {doc.comments.length} commentaire{doc.comments.length !== 1 ? 's' : ''}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex space-x-2 pt-2 border-t">
                        {doc.permissions?.can_download && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(doc)}
                            className="flex-1"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Télécharger
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setCommentDialogOpen(true);
                          }}
                        >
                          <MessageSquare className="h-3 w-3" />
                        </Button>

                        {doc.permissions?.can_delete && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(doc)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload un Document</DialogTitle>
            <DialogDescription>
              Taille maximum: 5 MB. Formats autorisés: PDF, Word, Excel, Images
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Sélection fichier */}
            <div>
              <Label htmlFor="file">Fichier *</Label>
              <Input
                id="file"
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.doc,.xls,.ppt,.pptx"
                className="mt-2"
              />
              {uploadFile && (
                <p className="text-sm text-gray-600 mt-1">
                  Sélectionné: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                </p>
              )}
            </div>

            {/* Catégorie */}
            <div>
              <Label htmlFor="category">Catégorie *</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(value) => setUploadForm({...uploadForm, category: value})}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMINISTRATIF">Administratif</SelectItem>
                  <SelectItem value="PEDAGOGIQUE">Pédagogique</SelectItem>
                  <SelectItem value="SCOLAIRE">Scolaire</SelectItem>
                  <SelectItem value="FINANCIER">Financier</SelectItem>
                  <SelectItem value="AUTRE">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Titre */}
            <div>
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                placeholder="Mon document"
                className="mt-2"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                placeholder="Description du document..."
                className="mt-2"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadDialogOpen(false);
                  resetUploadForm();
                }}
                disabled={uploading}
              >
                Annuler
              </Button>
              <Button onClick={handleUpload} disabled={uploading || !uploadFile}>
                {uploading ? 'Upload en cours...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Commentaires</DialogTitle>
            <DialogDescription>
              {selectedDocument?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Liste des commentaires */}
            {selectedDocument?.comments && selectedDocument.comments.length > 0 && (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {selectedDocument.comments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm">{comment.user_name}</span>
                      <span className="text-xs text-gray-500">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.comment}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Ajouter un commentaire */}
            <div>
              <Label htmlFor="comment">Ajouter un commentaire</Label>
              <Input
                id="comment"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Votre commentaire..."
                className="mt-2"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setCommentDialogOpen(false)}>
                Fermer
              </Button>
              <Button onClick={handleAddComment} disabled={!commentText.trim()}>
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentManagement;
